import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui';

import { AuthShell, StatusBadge } from '@/pages/auth/auth-shell';
import { SupportLinks } from '@/components/support-links';
import { isChunkLoadError, reloadForNewVersion } from '@/lib/chunk-reload';

/**
 * Red de seguridad de toda la app.
 *
 * Sin esto, un error de render en cualquier pantalla desmonta el árbol de
 * React entero y queda la página en blanco, sin explicación ni salida. Acá
 * se cae a una pantalla con la marca, un botón para recargar y el contacto
 * de soporte.
 *
 * Tiene que ser una clase: los error boundaries no existen como hook. Va
 * DENTRO del `BrowserRouter` (usa `AuthShell`, que lleva un `<Link>`) y
 * FUERA de `App`, para cubrir también el router y la hidratación de sesión.
 *
 * Los errores de event handlers y de promesas NO los atrapa React; esos
 * siguen yendo a `toast.error(...)` en cada pantalla, como hasta ahora.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Un trozo que no carga después de un deploy se arregla recargando: se
    // hace solo una vez (ver `lib/chunk-reload`) y, si vuelve a fallar, queda
    // la pantalla con el botón.
    if (isChunkLoadError(error) && reloadForNewVersion()) return;
    // Sin servicio de errores en este cliente: la consola es lo que queda.
    console.error('[urbania360] render error', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Después de un deploy, el HTML cacheado pide trozos que ya no existen:
    // el error es de red/carga, no de código, y recargar SÍ lo arregla.
    const isChunkError = isChunkLoadError(error);

    return (
      <AuthShell
        align="center"
        icon={
          <StatusBadge tone="danger">
            <TriangleAlert className="h-6 w-6" />
          </StatusBadge>
        }
        title={isChunkError ? 'Hay una versión nueva' : 'Algo salió mal'}
        subtitle={
          isChunkError
            ? 'Recarga la página para seguir con la versión más reciente.'
            : 'Tuvimos un problema inesperado. Recarga la página; si sigue pasando, escríbenos.'
        }
      >
        <Button
          type="button"
          size="lg"
          className="w-full"
          onClick={() => window.location.reload()}
        >
          Recargar la página
        </Button>
        <SupportLinks className="justify-center" />
      </AuthShell>
    );
  }
}
